/**
 * Users Settings Tab
 *
 * Admin-only tab for managing users:
 * - View all users with role, departments, status
 * - Invite new users
 * - Edit user role and departments
 * - Deactivate/reactivate users
 */

import React, { useState, useEffect } from 'react';
import { userAPI, UserDetail, UserUpdateAdmin, UserRole } from '../../services/api';
import InviteUserModal from '../InviteUserModal';
import { usePermissions } from '../../hooks/usePermissions';

const ROLE_LABELS: Record<UserRole, string> = {
  viewer: 'Viewer',
  supervisor: 'Supervisor',
  admin: 'Admin',
};

const ROLE_COLORS: Record<UserRole, string> = {
  viewer: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200',
  supervisor: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  admin: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300',
};

const UsersTab: React.FC = () => {
  const { user: currentUser } = usePermissions();
  const [users, setUsers] = useState<UserDetail[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  // Invite modal
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);

  // Inline edit state
  const [editingUserId, setEditingUserId] = useState<number | null>(null);
  const [editRole, setEditRole] = useState<UserRole>('viewer');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const userList = await userAPI.list();
      setUsers(userList);
    } catch (err: any) {
      const detail = err.response?.data?.detail;
      setError(
        Array.isArray(detail)
          ? detail.map((e: any) => e.msg || String(e)).join('; ')
          : typeof detail === 'string' ? detail : 'Failed to load users'
      );
    } finally {
      setLoading(false);
    }
  };

  const startEdit = (user: UserDetail) => {
    setEditingUserId(user.id);
    setEditRole(user.role);
    setActionError(null);
  };

  const cancelEdit = () => {
    setEditingUserId(null);
    setActionError(null);
  };

  const saveEdit = async () => {
    if (!editingUserId) return;
    try {
      setSaving(true);
      setActionError(null);
      const data: UserUpdateAdmin = {
        role: editRole,
      };
      await userAPI.update(editingUserId, data);
      setEditingUserId(null);
      await loadData();
    } catch (err: any) {
      const detail = err.response?.data?.detail;
      setActionError(
        Array.isArray(detail)
          ? detail.map((e: any) => e.msg || String(e)).join('; ')
          : typeof detail === 'string' ? detail : 'Failed to update user'
      );
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (user: UserDetail) => {
    try {
      setActionError(null);
      if (user.is_active) {
        await userAPI.deactivate(user.id);
      } else {
        await userAPI.reactivate(user.id);
      }
      await loadData();
    } catch (err: any) {
      const detail = err.response?.data?.detail;
      setActionError(
        Array.isArray(detail)
          ? detail.map((e: any) => e.msg || String(e)).join('; ')
          : typeof detail === 'string' ? detail : 'Failed to update user status'
      );
    }
  };

  const handleDeleteUser = async (user: UserDetail) => {
    if (!window.confirm(`Permanently delete ${user.full_name}? This cannot be undone.`)) return;
    try {
      setActionError(null);
      await userAPI.delete(user.id);
      await loadData();
    } catch (err: any) {
      const detail = err.response?.data?.detail;
      setActionError(
        Array.isArray(detail)
          ? detail.map((e: any) => e.msg || String(e)).join('; ')
          : typeof detail === 'string' ? detail : 'Failed to delete user'
      );
    }
  };

  const formatDate = (dateStr: string | null): string => {
    if (!dateStr) return 'Never';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className="rounded-lg shadow p-6" style={{ background: 'var(--bg-secondary)' }}>
        <div className="flex items-center gap-2">
          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
          <span style={{ color: 'var(--text-secondary)' }}>Loading users...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>
          Users ({users.length})
        </h2>
        <button
          onClick={() => setIsInviteModalOpen(true)}
          className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
        >
          Invite User
        </button>
      </div>

      {error && (
        <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-300 text-sm">
          {error}
        </div>
      )}

      {actionError && (
        <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-300 text-sm">
          {actionError}
        </div>
      )}

      {/* Users Table */}
      <div className="rounded-lg shadow overflow-hidden" style={{ background: 'var(--bg-secondary)' }}>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border-color)' }}>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--text-secondary)' }}>Name</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--text-secondary)' }}>Email</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--text-secondary)' }}>Role</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--text-secondary)' }}>Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--text-secondary)' }}>Last Login</th>
                <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--text-secondary)' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr
                  key={user.id}
                  style={{ borderBottom: '1px solid var(--border-color)' }}
                  className={!user.is_active ? 'opacity-60' : ''}
                >
                  <td className="px-4 py-3 text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                    {user.full_name}
                    {user.id === currentUser?.id && (
                      <span className="ml-2 text-xs px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">You</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm" style={{ color: 'var(--text-secondary)' }}>
                    {user.email}
                  </td>

                  {/* Role */}
                  <td className="px-4 py-3 text-sm">
                    {editingUserId === user.id ? (
                      <select
                        value={editRole}
                        onChange={(e) => setEditRole(e.target.value as UserRole)}
                        className="px-2 py-1 text-xs border rounded"
                        style={{ background: 'var(--bg-primary)', color: 'var(--text-primary)', borderColor: 'var(--border-color)' }}
                      >
                        <option value="viewer">Viewer</option>
                        <option value="supervisor">Supervisor</option>
                        <option value="admin">Admin</option>
                      </select>
                    ) : (
                      <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${ROLE_COLORS[user.role]}`}>
                        {ROLE_LABELS[user.role]}
                      </span>
                    )}
                  </td>

                  {/* Status */}
                  <td className="px-4 py-3 text-sm">
                    <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${
                      user.is_active
                        ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                        : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                    }`}>
                      {user.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>

                  {/* Last Login */}
                  <td className="px-4 py-3 text-sm" style={{ color: 'var(--text-secondary)' }}>
                    {formatDate(user.last_login)}
                  </td>

                  {/* Actions */}
                  <td className="px-4 py-3 text-sm text-right">
                    {editingUserId === user.id ? (
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={saveEdit}
                          disabled={saving}
                          className="px-3 py-1 text-xs font-medium text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50"
                        >
                          {saving ? 'Saving...' : 'Save'}
                        </button>
                        <button
                          onClick={cancelEdit}
                          disabled={saving}
                          className="px-3 py-1 text-xs font-medium rounded"
                          style={{ background: 'var(--bg-primary)', color: 'var(--text-primary)' }}
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <div className="flex justify-end gap-2">
                        {user.is_active ? (
                          <>
                            <button
                              onClick={() => startEdit(user)}
                              className="px-3 py-1 text-xs font-medium rounded hover:bg-gray-100 dark:hover:bg-gray-700"
                              style={{ color: 'var(--text-primary)' }}
                            >
                              Edit
                            </button>
                            {user.id !== currentUser?.id && (
                              <button
                                onClick={() => handleToggleActive(user)}
                                className="px-3 py-1 text-xs font-medium rounded text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                              >
                                Deactivate
                              </button>
                            )}
                          </>
                        ) : user.id !== currentUser?.id ? (
                          <>
                            <button
                              onClick={() => handleToggleActive(user)}
                              className="px-3 py-1 text-xs font-medium rounded text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20"
                            >
                              Reactivate
                            </button>
                            <button
                              onClick={() => handleDeleteUser(user)}
                              className="px-3 py-1 text-xs font-medium rounded text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                            >
                              Delete
                            </button>
                          </>
                        ) : null}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Invite User Modal */}
      <InviteUserModal
        isOpen={isInviteModalOpen}
        onClose={() => setIsInviteModalOpen(false)}
        onUserInvited={loadData}
      />
    </div>
  );
};

export default UsersTab;
